"use client"

import { useEffect, useState } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { AURORA_FLAG_CACHE_KEY } from "dashboard/lib/aurora-flag-cache"
import {
  isPostAuroraCutoffSignup,
  resolveAuroraEnabled,
} from "dashboard/lib/aurora-access"
import { useClerkCreatedAt } from "core/hooks/use-clerk-created-at"

const FLAG_KEY = "aurora_enabled"
// Deadline for both inputs below, so neither a PostHog-blocked user (ad blockers, privacy extensions) nor a stalled Clerk hangs on a spinner.
const RESOLVE_TIMEOUT_MS = 3000

function readCache(): boolean | undefined {
  if (typeof window === "undefined") return undefined
  try {
    const v = window.localStorage.getItem(AURORA_FLAG_CACHE_KEY)
    if (v === "true") return true
    if (v === "false") return false
    return undefined
  } catch {
    return undefined
  }
}

function writeCache(value: boolean): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(AURORA_FLAG_CACHE_KEY, String(value))
  } catch {
    // localStorage may be disabled (private mode, quota); skip silently.
  }
}

// Returns aurora_enabled, falling back to a localStorage cache for synchronous first-render values, then to `false` after RESOLVE_TIMEOUT_MS.
// Accounts created after AURORA_ALL_SIGNUPS_CUTOFF are always enabled — see aurora-access.ts.
export function useAuroraEnabled(): boolean | undefined {
  const live = useFeatureFlagEnabled(FLAG_KEY)
  const { createdAt, isLoaded: isSignupDateLoaded } = useClerkCreatedAt()
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (typeof live === "boolean") writeCache(live)
  }, [live])

  useEffect(() => {
    const t = setTimeout(() => {
      setTimedOut(true)
    }, RESOLVE_TIMEOUT_MS)
    return () => {
      clearTimeout(t)
    }
  }, [])

  // Live value wins, then the cache (for a synchronous first render), then off.
  const flagValue = live ?? readCache() ?? (timedOut ? false : undefined)

  // Past the deadline, an unresolved signup date counts as pre-cutoff so the
  // flag alone decides — better than waiting on Clerk forever.
  const isPostCutoffSignup =
    isSignupDateLoaded || timedOut
      ? isPostAuroraCutoffSignup(createdAt)
      : undefined

  return resolveAuroraEnabled(flagValue, isPostCutoffSignup)
}
